import { useState } from 'react';
import { MRT_SortingState } from 'material-react-table';
import { ChevronDown, ChevronUp, RotateCcw, Search, SlidersHorizontal } from 'lucide';

//MUI
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

//common
import Icon from 'common/components/Icon';
import SchemaForm from 'common/SchemaForm';
import { STATUS_OPTIONS } from 'common/constants';
import { organizationTypes } from 'enum/combobox';
import { removeEmptyKeys } from 'common/utils';

export interface Filters {
  orgType: string; // Phân loại tổ chức
  inactive?: boolean; // Trạng thái quản trị
  name: string;
}

export interface DepartmentFilterModel {
  name?: string;
  orgType?: string;
  inactive?: string;
}

export interface SearchParams {
  pageSize: number;
  pageIndex: number;
  sorting: MRT_SortingState;
  filters: DepartmentFilterModel;
}

export interface FilterProps {
  filters: DepartmentFilterModel;
  onSetFilters: (filters) => void;
  onClearFilters: () => void;
}

const FilterBox = ({ filters, onSetFilters, onClearFilters }: FilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handlePopoverClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;
  const filtersCount = Object.keys(removeEmptyKeys(filters)).length;

  return (
    <>
      <SchemaForm<DepartmentFilterModel>
        formData={filters}
        onSubmit={onSetFilters}
        schema={{
          fields: ({ formProps: { intl, isMobile } }) => {
            return {
              searchText: {
                mode: 'node',
                hidden: isMobile,
                render: (
                  <Typography sx={{ mt: 1 }}>{intl.formatMessage({ id: 'search' })}:</Typography>
                ),
                propsWrapper: { mobile: undefined },
              },
              name: {
                mode: 'text-field',
                placeholder: intl.formatMessage({ id: 'department.search' }),
                propsWrapper: { mobile: true },
              },
              submit: {
                mode: 'button',
                type: 'submit',
                variant: 'contained',
                startIcon: <Icon icon={Search} size={16} />,
                'aria-label': intl.formatMessage({ id: 'search' }),
                children: !isMobile && intl.formatMessage({ id: 'search' }),
                propsWrapper: { mobile: undefined },
              },
              filter: {
                mode: 'button',
                variant: 'outlined',
                'aria-label': intl.formatMessage({ id: 'filter' }),
                children: !isMobile && intl.formatMessage({ id: 'filter' }),
                startIcon: (
                  <Badge badgeContent={filtersCount} variant="standard" color="primary">
                    <Icon icon={SlidersHorizontal} size={16} />
                  </Badge>
                ),
                endIcon: !isMobile && <Icon icon={open ? ChevronUp : ChevronDown} size={16} />,
                onClick: handleClick,
                propsWrapper: { mobile: undefined },
              },
              reset: {
                mode: 'button',
                // hidden: isMobile,
                variant: 'outlined',
                'aria-label': 'Đặt lại bộ lọc',
                children: <Icon icon={RotateCcw} />,
                onClick: onClearFilters,
                propsWrapper: { mobile: undefined },
              },
            };
          },
        }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        slotProps={{
          paper: {
            variant: 'outlined',
            elevation: 0,
            sx: {
              borderColor: 'primary.main',
              p: 2,
              mt: 2,
              width: 280,
            },
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <SchemaForm<DepartmentFilterModel>
          key={`dept-popover-${filters.orgType ?? ''}-${filters.inactive ?? ''}`}
          formData={filters}
          schema={{
            fields: ({ formProps: { intl } }) => {
              return {
                orgType: {
                  mode: 'select',
                  label: intl.formatMessage({ id: 'department.organizationType' }),
                  rawOptions: true,
                  options: [
                    { id: '', name: intl.formatMessage({ id: 'all' }) },
                    ...organizationTypes,
                  ],
                  disableClearBtn: true,
                  onChange: (orgTypeId: string) => {
                    onSetFilters({
                      ...filters,
                      orgType: orgTypeId || '',
                    });
                  },
                },
                inactive: {
                  mode: 'select',
                  label: intl.formatMessage({ id: 'statusAdmin' }),
                  options: [{ id: '', name: intl.formatMessage({ id: 'all' }) }, ...STATUS_OPTIONS],
                  disableClearBtn: true,
                  onChange: (val: boolean | '') => {
                    onSetFilters({
                      ...filters,
                      inactive: val === '' ? '' : (val as any),
                    });
                  },
                },
              };
            },
          }}
        />
      </Popover>
    </>
  );
};
export default FilterBox;
